'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonFeature {
  name: string;
  minerals: boolean;
  equipment: boolean;
  personnel: boolean;
  customs: boolean;
}

interface ServiceComparisonProps {
  features: ComparisonFeature[];
}

const ServiceComparison = ({ features }: ServiceComparisonProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services = [
    { id: 'minerals', name: 'Minerals Transport', icon: 'CubeIcon', color: 'from-golden-light to-golden-dark' },
    { id: 'equipment', name: 'Heavy Equipment', icon: 'TruckIcon', color: 'from-golden-mid to-golden-dark' },
    { id: 'personnel', name: 'Personnel Movement', icon: 'UserGroupIcon', color: 'from-golden-light to-golden-mid' },
    { id: 'customs', name: 'Customs Brokerage', icon: 'DocumentCheckIcon', color: 'from-golden-dark to-golden-mid' },
  ];

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
              Service Comparison Matrix
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Compare capabilities across our specialized logistics services
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-lg p-8 animate-pulse">
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Service Comparison Matrix
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Compare capabilities across our specialized logistics services
          </p>
        </div>
        
        <div className="bg-card rounded-2xl shadow-lg overflow-hidden border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-primary via-primary/95 to-primary/90">
                  <th className="px-6 py-4 text-left">
                    <span className="font-headline font-semibold text-white">Capability</span>
                  </th>
                  {services.map((service) => (
                    <th
                      key={service.id}
                      className="px-6 py-4 text-center cursor-pointer hover:bg-white/10 transition-colors duration-300"
                      onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-golden-md`}>
                          <Icon name={service.icon as any} size={24} className="text-primary" />
                        </div>
                        <span className="font-headline font-semibold text-sm text-white">{service.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr
                    key={index}
                    className={`border-t border-border hover:bg-muted/50 transition-colors duration-300 ${
                      index % 2 === 0 ? 'bg-background' : 'bg-card'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className="font-body text-foreground">{feature.name}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.minerals ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark">
                          <Icon name="CheckIcon" size={16} className="text-primary" />
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                          <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.equipment ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark">
                          <Icon name="CheckIcon" size={16} className="text-primary" />
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                          <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.personnel ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark">
                          <Icon name="CheckIcon" size={16} className="text-primary" />
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                          <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {feature.customs ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark">
                          <Icon name="CheckIcon" size={16} className="text-primary" />
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                          <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceComparison;